import React from "react";
import { LuPlus } from "react-icons/lu";
import { useWebsiteEditorInspectorOptional } from "./WebsiteEditorInspectorContext";
import { useWebsiteEditorMobileSelectionOptional } from "./websiteEditorMobileSelection";
import { useCoarsePointer } from "./useWebsiteEditorMobile";
import { canOpenAddComponent } from "./customWebsitePages";
import "./websiteEditorChrome.css";

/**
 * Standalone "Add component" control — same hover line + pill as
 * `WebsiteEditorInsertPoint`, for end-of-page / Featured / empty slots
 * (not the zero-height between-section inserts).
 */
export function WebsiteEditorAddComponentBar({
  insertAt = -1,
  label = "Add component",
  hideOnMobile = false,
}: {
  /** Visible-section index to insert at. `-1` appends (default for freeform pages). */
  insertAt?: number;
  label?: string;
  /**
   * Hide on mobile editors — the mobile FAB + selected-section insert points
   * cover add. Shop Featured bars stay visible when this is false.
   */
  hideOnMobile?: boolean;
} = {}) {
  const ctx = useWebsiteEditorInspectorOptional();
  const mobileCtx = useWebsiteEditorMobileSelectionOptional();
  const coarsePointer = useCoarsePointer();
  const [hov, setHov] = React.useState(false);

  if (
    !ctx?.enabled ||
    ctx.locked ||
    !canOpenAddComponent(ctx.currentPage ?? "")
  ) {
    return null;
  }
  if (hideOnMobile && mobileCtx) {
    return null;
  }

  // Mobile shell / touch: keep the pill visible. Hover-only reveal left a
  // clickable row with no label when the device still reported hover.
  const shown = hov || coarsePointer || Boolean(mobileCtx);
  const touchCue = coarsePointer || Boolean(mobileCtx);
  const text = String(label || "Add component").replace(/^\+\s*/, "");

  return (
    <div
      className={`mte-we-insert mte-we-insert--standalone${
        shown ? " is-hot" : ""
      }${touchCue ? " is-touch" : ""}`}
      data-mte-insert-at={insertAt}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      onClick={() => ctx.openAdd(insertAt)}
    >
      <div className="mte-we-insert-hit" aria-hidden />
      <span className="mte-we-insert-line" />
      <button type="button" className="mte-we-insert-btn">
        <LuPlus strokeWidth={2.5} aria-hidden /> {text}
      </button>
    </div>
  );
}
